import React, { useMemo, useState } from "react";
import Joi from "joi";
import { Box, HStack, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { EApiWatchdogFrequencyId, IWatchdog } from "utils/watchdog";
import { EApiDispositionId } from "utils/offer";
import ChangeWatchdogApiEndpoint from "utils/watchdog/api/ChangeWatchdog";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import useApiEndpoint from "hooks/useApiEndpoint";
import { DISPOSITIONS } from "@ud/config/dispositions";
import { useAlertModal } from "modals/useAlertModal";
import FormRadio from "components/Chakra/FormRadio";
import FormChoice from "components/Chakra/FormChoice";
import FormRange from "components/Chakra/FormRange";
import { dispositionsOptions } from "utils/search/types";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Button from "components/Chakra/Button";

interface IFormValues {
  name: string;
  min: number;
  max: number;
}

const formSchema = Joi.object({
  name: Joi.string().required(),
  min: Joi.number().integer().required().label("od"),
  max: Joi.number()
    .integer()
    .min(Joi.ref("min", { render: true }))
    .required(),
});

interface IProps {
  edit: IWatchdog;
  onCancel: () => void;
  onSubmit?: () => void;
}

const EditForm = ({ edit, onSubmit, onCancel }: IProps): JSX.Element => {
  const { openModal: openAlertModal } = useAlertModal();
  const { callEndpoint: changeWatchdogApi } = useApiEndpoint(
    ChangeWatchdogApiEndpoint,
  );
  const { t } = useTranslation();

  const frequencyOptions = useMemo(
    () => [
      {
        value: EApiWatchdogFrequencyId.IMMEDIATE,
        label: t("watchdogFrequency.immediate"),
        badge: "Nejoblíbenější",
      },
      {
        value: EApiWatchdogFrequencyId.ONCE_PER_DAY,
        label: t("watchdogFrequency.oncePerDay"),
      },
      {
        value: EApiWatchdogFrequencyId.ONCE_PER_WEEK,
        label: t("watchdogFrequency.oncePerWeek"),
      },
      {
        value: EApiWatchdogFrequencyId.NEVER,
        label: t("watchdogFrequency.never"),
      },
    ],
    [t],
  );

  const [frequency, setFrequency] = useState<string>(edit.frequencyId);
  const [disposition, setDisposition] = useState<Array<string>>(
    edit.filter.dispositions.filter(Boolean).map((editDisposition) => {
      const wdDisposition = DISPOSITIONS.find(({ apiIds }) =>
        apiIds.includes(editDisposition),
      )?.queryValue.toString();

      return wdDisposition || "";
    }),
  );

  const { handleSubmit, registerWithError } = useForm<IFormValues>(formSchema, {
    defaultValues: {
      name: edit?.name,
      min: edit.filter.priceFrom,
      max: edit.filter.priceTo,
    },
    keepDataOnSuccess: true,
  });

  const handleData: OnFormSubmitType<IFormValues> = async (
    data,
  ): Promise<void> => {
    if (!edit) {
      return;
    }

    const res = await changeWatchdogApi({
      frequencyId: frequency as EApiWatchdogFrequencyId,
      name: data.name,
      id: edit.id,
      filter: {
        offerTypeId: 1,
        priceTo: data.max || 0,
        priceFrom: data.min || 0,
        dispositions: disposition.reduce(
          (
            prev: Array<EApiDispositionId>,
            next: string,
          ): Array<EApiDispositionId> => {
            const wdObj = DISPOSITIONS.find(
              ({ queryValue }) => queryValue === next,
            );
            if (!wdObj) {
              return prev;
            }

            return [
              ...prev,
              ...wdObj.apiIds.map((id) => id.toString()),
            ] as Array<EApiDispositionId>;
          },
          [],
        ),
        acreageFrom: null,
        acreageTo: null,
        bounds: edit.filter.bounds,
        furnishing: [],
        conveniences: [],
      },
    });

    if (res.errors.length !== 0) {
      openAlertModal({
        animation: "error",
        title: "Opravdu chcete Hlídacího psa smazat?",
        submitText: "Pokračovat",
      });
    } else {
      openAlertModal({
        animation: "check",
        title: "Změny byly uloženy",
        submitText: "Děkuji",
      });
    }
    onSubmit && onSubmit();
  };

  return (
    <form onSubmit={handleSubmit(handleData)} data-test="editDogInfo">
      <Box
        background="ud-white"
        borderRadius={6}
        boxShadow="0 0 10px 0 rgba(0, 0, 0, 0.19)"
        paddingY={12}
        paddingX={{ base: 5, sm: 12 }}
      >
        <Stack spacing={0}>
          <AnimatedInput
            data-test="dogNameEditInput"
            {...registerWithError("name")}
            label="Jméno"
            required
          />
          <Stack spacing={4}>
            <Text variant="small_1" textTransform="uppercase" fontWeight="500">
              Interval upozornění
            </Text>
            <Box
              borderWidth="1px"
              borderStyle="solid"
              borderColor="ud-grey-300"
              borderRadius={4}
              padding={4}
            >
              <FormRadio
                items={frequencyOptions}
                value={frequency}
                onChange={setFrequency}
              />
            </Box>
          </Stack>
          <Stack spacing={4} pt={4} data-test="dispozitionEditSelect">
            <Text variant="small_1" textTransform="uppercase" fontWeight="500">
              Dispozice
            </Text>
            <FormChoice
              options={dispositionsOptions}
              values={disposition}
              onChange={(value) => {
                setDisposition((prevState) => {
                  if (prevState.includes(value)) {
                    return prevState.filter((disp) => disp !== value);
                  }

                  return [value, ...prevState];
                });
              }}
            />
          </Stack>
          <Stack spacing={4} pt={4}>
            <Text variant="small_1" textTransform="uppercase" fontWeight="500">
              Cena
            </Text>
            <FormRange
              minInputProps={{ ...registerWithError("min"), required: true }}
              maxInputProps={{ ...registerWithError("max"), required: true }}
            />
          </Stack>
        </Stack>

        <HStack alignItems="center" justify="center" spacing={5}>
          <Button
            data-test="cancelEditsButton"
            variant="link"
            onClick={onCancel}
          >
            Zrušit
          </Button>
          <Button data-test="submitEditsButton" type="submit">
            Upravit
          </Button>
        </HStack>
      </Box>
    </form>
  );
};

export default EditForm;
